/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

// Import analytics for GA integration
import analytics from './analytics';

const LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
  INFO: 'info',
  DEBUG: 'debug'
};

// Environment-based logging configuration
const ENV = process.env.NODE_ENV || 'development';
const LOG_LEVEL = process.env.LOG_LEVEL || (ENV === 'production' ? 'info' : 'debug');

// Structured log formatter
const formatLogMessage = (level, event, details = {}, error = null) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    level,
    service: 'safety-app',
    environment: ENV,
    event,
    details: {
      ...details,
      // Add application context
      page: window.location.pathname,
      sessionId: localStorage.getItem('sessionId')
    }
  };

  // Error handling with stack traces in development
  if (error) {
    logEntry.error = {
      message: error.message,
      stack: ENV === 'development' ? error.stack : undefined,
      code: error.code
    };
  }

  return logEntry;
};

// Determine if a log level should be processed
const shouldLog = (messageLevel) => {
  const levels = Object.values(LOG_LEVELS);
  const configuredLevelIndex = levels.indexOf(LOG_LEVEL);
  const messageLevelIndex = levels.indexOf(messageLevel);
  return messageLevelIndex <= configuredLevelIndex;
};

// Map significant events to GA tracking
const trackEventToGA = (level, event, details) => {
  // Only track important events to avoid GA spam
  const trackableEvents = [
    'user_login_success', 'user_logout', 'user_registration',
    'test_info_fetched', 'test_submitted', 'test_completion_failed',
    'session_restored', 'session_establishment_failed',
    'api_error', 'authentication_failed'
  ];
  
  if (trackableEvents.includes(event)) {
    switch (event) {
      case 'api_error':
        analytics.trackError('api_error', details.error || 'Unknown API error', details.endpoint);
        break;
      case 'authentication_failed':
        analytics.trackEvent('authentication_error', {
          category: 'errors',
          label: 'authentication_failed'
        });
        break;
      case 'test_completion_failed':
        analytics.trackError('test_submission_error', details.error || 'Test submission failed');
        break;
      default:
        // Track as custom event for other important events
        analytics.trackEvent(event, {
          category: level === 'error' ? 'errors' : 'system_events',
          label: event,
          ...details
        });
    }
  }
};

const logger = {
  error: (event, details, error) => {
    if (shouldLog(LOG_LEVELS.ERROR)) {
      const logMessage = formatLogMessage(LOG_LEVELS.ERROR, event, details, error);
      console.error(JSON.stringify(logMessage));

      // Track significant errors to GA
      trackEventToGA('error', event, details);

      // Optional: Send errors to monitoring service
      if (ENV === 'production') {
        // Example: errorMonitoringService.capture(logMessage);
      }
    }
  },

  warn: (event, details) => {
    if (shouldLog(LOG_LEVELS.WARN)) {
      const logMessage = formatLogMessage(LOG_LEVELS.WARN, event, details);
      console.warn(JSON.stringify(logMessage));
    }
  },

  info: (event, details) => {
    if (shouldLog(LOG_LEVELS.INFO)) {
      const logMessage = formatLogMessage(LOG_LEVELS.INFO, event, details);
      console.info(JSON.stringify(logMessage));
      
      // Track significant info events to GA
      trackEventToGA('info', event, details);
    }
  },

  debug: (event, details) => {
    if (shouldLog(LOG_LEVELS.DEBUG)) {
      const logMessage = formatLogMessage(LOG_LEVELS.DEBUG, event, details);
      console.debug(JSON.stringify(logMessage));
    }
  }
};

// Usage examples:
/*
logger.info('user_action', {
  userId: 'user123',
  action: 'test_started',
  testId: 'test456'
});

logger.error('api_error', 
  { endpoint: '/api/tests', method: 'GET' },
  new Error('Network request failed')
);
*/

export default logger;